import { normalizeOrganizationId } from "@/shared/organizations/organization-id";
import { useQuery } from "@tanstack/react-query";
import { AUDITED_READ_OPTIONS } from "@/shared/api/audited-read";
import { httpClient } from "@/shared/api/http-client";
import { keepRowsWithinScope } from "@/shared/api/scoped-placeholder";
import { buildMembersQuery, organizationMembersPath } from "./build-organizations-query";
import { ORGANIZATIONS_QUERY_KEY } from "./constants";
import type { AdminOrganizationMembersList, MembersListParams } from "./types";

function membersKey(organizationId: string, params: MembersListParams) {
  return [
    ...ORGANIZATIONS_QUERY_KEY,
    "organization",
    normalizeOrganizationId(organizationId) ?? "",
    "members",
    params.status ?? "all",
    params.role ?? "all",
    params.page,
  ] as const;
}

/**
 * `GET /api/admin/organizations/:id/members` — every role and status, oldest
 * first, display names only (the API omits emails and phones). Audited and
 * throttled (60/min) by the API: no retry, no refetch on focus.
 *
 * Rows stay on screen while paging, but never stand in for the result of a
 * different filter or organization.
 */
export function useOrganizationMembers(organizationId: string, params: MembersListParams) {
  const queryKey = membersKey(organizationId, params);
  return useQuery({
    queryKey,
    queryFn: () =>
      httpClient<AdminOrganizationMembersList>(
        `${organizationMembersPath(organizationId)}${buildMembersQuery(params)}`,
      ),
    enabled: normalizeOrganizationId(organizationId) !== null,
    placeholderData: keepRowsWithinScope<AdminOrganizationMembersList>(queryKey),
    ...AUDITED_READ_OPTIONS,
  });
}
